import { navigate } from 'gatsby';
import { StoryblokComponent } from 'gatsby-source-storyblok';
import React, { memo, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import spinner from '../../../assets/icons/spinner.svg';
import { LayoutContext } from '../../../contexts/LayoutContext';
import { PageContext } from '../../../contexts/PageContext';
import { QuoteContext } from '../../../contexts/Quote';
import { useGetCostBreakdown } from '../../../hooks/useGetCostBreakdown';
import { useGetQuote } from '../../../hooks/useGetQuote';
import { Blok } from '../../../interfaces/Blok.interface';
import { LinkProps } from '../../../interfaces/LinkProps.interface';

import {
  Clause,
  QuoteAcceptanceRequestPayload,
} from '../../../services/quote.interfaces';
import {
  getQuoteModalState,
  setModalState,
} from '../../../store/slices/quote/quote.store';
import Heading, { HeadingTypes } from '../../Heading/Heading';
import QuoteAPIErrorList from '../../QuoteForm/QuoteAPIErrors';
import QuoteForm from '../../QuoteForm/QuoteForm';
import QuoteFormHeadingBar from '../../QuoteFormHeadingBar/QuoteFormHeadingBar';
import QuoteFormTabBar from '../../QuoteFormTabBar/QuoteFormTabBar';
import TestingGridMolecule from '../../TestingGrid/TestingGrid.molecule';
import Col from '../../Utils/Col/Col';
import Container from '../../Utils/Container/Container';
import Grid from '../../Utils/Grid/Grid';
import QuoteStageModal from '../QuoteStageModal/QuoteStageModal';
import QuoteStageThreePageLoadingState from './QuoteStageThreePageLoadingState';
import { postQuoteAcceptance } from '../../../services/quote.service';
import { postQuoteOverviewToDataLayer } from '../../../utils/postQuoteOverviewToDataLayer/postQuoteOverviewToDataLayer.util';

interface QuoteStageThreePageBlokProps extends Blok {
  stage_one_tab_link: LinkProps[];
  stage_two_tab_link: LinkProps[];
  stage_three_tab_label: string;
  heading: string;
  left_column: Blok[];
  right_column: Blok[];
}

interface QuoteStageThreePageProps {
  blok: QuoteStageThreePageBlokProps;
}

const mailIconStyle = {
  WebkitMaskImage: "url('/svgs/blue-mail-icon.svg')",
  maskImage: "url('/svgs/blue-mail-icon.svg')",
};

const getPropertyInformation = (values: any, transactionType: string) => {
  switch (transactionType) {
    case 'Buy':
      return {
        buyInformation: {
          isAddressKnown: false,
          address: {
            addressLine2: values['buy-address-addressLine2'],
            city: values['buy-address-city'],
            postcode: values['buy-address-postcode'],
            street: values['buy-address-street'],
          },
        },
      };

    case 'Sell':
      return {
        sellInformation: {
          isSellAddressSameAsCustomerAddress:
            values['isSellAddressSameAsCustomerAddress'],
          address: values['isSellAddressSameAsCustomerAddress']
            ? {
                addressLine2: values['customer-address-addressLine2'],
                city: values['customer-address-city'],
                postcode: values['customer-address-postcode'],
                street: values['customer-address-street'],
              }
            : {
                addressLine2: values['sell-address-addressLine2'],
                city: values['sell-address-city'],
                postcode: values['sell-address-postcode'],
                street: values['sell-address-street'],
              },
        },
      };
    case 'BuyAndSell':
      return {
        buyAndSellInformation: {
          isSellAddressSameAsCustomerAddress:
            values['isSellAddressSameAsCustomerAddress'],
          isBuyAddressKnown: false,
          buyAddress: {
            addressLine2: values['buy-address-addressLine2'],
            city: values['buy-address-city'],
            postcode: values['buy-address-postcode'],
            street: values['buy-address-street'],
          },
          sellAddress: values['isSellAddressSameAsCustomerAddress']
            ? {
                addressLine2: values['customer-address-addressLine2'],
                city: values['customer-address-city'],
                postcode: values['customer-address-postcode'],
                street: values['customer-address-street'],
              }
            : {
                addressLine2: values['sell-address-addressLine2'],
                city: values['sell-address-city'],
                postcode: values['sell-address-postcode'],
                street: values['sell-address-street'],
              },
        },
      };
    case 'Remortgage':
      return {
        remortgageInformation: {
          isRemortgageAddressSameAsCustomerAddress:
            values['isRemortgageAddressSameAsCustomerAddress'],
          address: values['isRemortgageAddressSameAsCustomerAddress']
            ? {
                addressLine2: values['customer-address-addressLine2'],
                city: values['customer-address-city'],
                postcode: values['customer-address-postcode'],
                street: values['customer-address-street'],
              }
            : {
                addressLine2: values['remortgage-address-addressLine2'],
                city: values['remortgage-address-city'],
                postcode: values['remortgage-address-postcode'],
                street: values['remortgage-address-street'],
              },
        },
      };
  }
};

const ApiErrors = ({ errors }: any) => {
  if (errors?.response?.status === 409) {
    return (
      <div className="text-text/body-primary py-4">Quote already accepted.</div>
    );
  }

  if (errors?.response?.status === 400) {
    return <QuoteAPIErrorList errors={errors.response.data as any} />;
  }
};

const getPropertyType = (transactionType: string) => {
  const transactionTypeMap = new Map([
    ['BuyAndSell', 'buying-and-selling'],
    ['Buy', 'buying'],
    ['Sell', 'selling'],
    ['Remortgage', 'remortgaging'],
  ]);

  return transactionTypeMap.get(transactionType);
};

const QuoteStageThreePage: React.FC<QuoteStageThreePageProps> = ({ blok }) => {
  const {
    stage_one_tab_link,
    stage_two_tab_link,
    stage_three_tab_label,
    heading,
    left_column,
    right_column,
  } = blok;

  const dispatch = useDispatch();
  const { header, footer } = useContext(LayoutContext);
  const modalState = useSelector(getQuoteModalState);

  let searchParams: URLSearchParams = new URLSearchParams();
  const [quoteParams, setQuoteParams] = useState({
    quoteReference: '',
    trackingRef: '',
  });

  const [hasErrors, setHasErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [apiErrors, setApiErrors] = useState<any>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    searchParams = new URLSearchParams(window.location.search);

    if (!searchParams.get('quotation') && !searchParams.get('TrackingRef')) {
      navigate(
        `/instant-quote/quote-stage-one/?quotation=${quoteParams.quoteReference}&TrackingRef=${quoteParams.trackingRef}}`
      );
    }

    setQuoteParams({
      quoteReference: searchParams.get('quotation') || '',
      trackingRef: searchParams.get('TrackingRef') || '',
    });
  }, []);

  const [quote, quoteErrors, quoteIsLoading] = useGetQuote(
    quoteParams.quoteReference,
    quoteParams.trackingRef
  );

  const [costBreakdown, costBreakdownErrors, costBreakdownIsLoading] =
    useGetCostBreakdown(quoteParams.quoteReference, quoteParams.trackingRef);

  useEffect(() => {
    if (quote && costBreakdown) {
      setIsLoading(false);
    }
  }, [quote, costBreakdown]);

  useEffect(() => {
    setHasErrors(quoteErrors || costBreakdownErrors);
  }, [quoteErrors, costBreakdownErrors]);

  useEffect(() => {
    if (quote && costBreakdown) {
      handlePostQuoteOverviewToDataLater('quote_step3_view');
    }
  }, [quote && costBreakdown]);

  const handlePostQuoteOverviewToDataLater = (eventName: string): void => {
    postQuoteOverviewToDataLayer(
      eventName,
      quoteParams.quoteReference!,
      quoteParams.trackingRef!,
      costBreakdown!
    );
  };

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    handlePostQuoteOverviewToDataLater('quote_step3_instruct');
    const transactionType = quote.transactionType;

    const acceptPayload: QuoteAcceptanceRequestPayload = {
      propertyInformation: {},
      customerDetails: {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        telephone: values.telephone,
        title: values.title,
      },
      customerAddress: {
        addressLine2: values['customer-address-addressLine2'],
        city: values['customer-address-city'],
        postcode: values['customer-address-postcode'],
        street: values['customer-address-street'],
      },
      homeDocumentPack: {
        isHomeDocumentPackRequested: false,
      },
    };
    const propertyInformation = getPropertyInformation(values, transactionType);
    try {
      const result = await postQuoteAcceptance(
        quoteParams.quoteReference,
        {
          ...acceptPayload,
          propertyInformation: { ...propertyInformation },
        },
        quoteParams.trackingRef
      );
      setApiErrors([]);

      if (result.status === 202) {
        navigate(`/instant-quote/quote-request-pending/`);
        return;
      } else if (result.status === 403) {
        navigate(`/instant-quote/quote-not-found/`);

        return;
      } else {
        navigate(
          `/instant-quote/quote-complete/?quotation=${quoteParams.quoteReference}&TrackingRef=${quoteParams.trackingRef}`
        );

        return;
      }
    } catch (error: any) {
      setApiErrors(error);
      setIsSubmitting(false);

      if (error?.response?.status == 202) {
        navigate(`/instant-quote/quote-request-pending/`);
        return;
      }

      if (error?.response?.status == 403) {
        navigate(`/instant-quote/quote-not-found/`);
        return;
      }
    }
  };

  if (hasErrors) {
    navigate(`/instant-quote/quote-not-found/`);
  }

  return (
    <QuoteContext.Provider
      value={{
        quoteDetails: quote,
        quoteCostBreakdown: costBreakdown,
        quoteReference: quoteParams.quoteReference,
        trackingRef: quoteParams.quoteReference,
      }}
    >
      <PageContext.Provider
        value={{
          headerIsTransparent: false,
          isHeaderSearchFunctionalityRequired: true,
          showContactBar: false,
        }}
      >
        <div
          className="flex flex-col h-screen justify-between"
          data-theme="alt"
        >
          {header && (
            <StoryblokComponent blok={header.content} key={header._uid} />
          )}
          {isLoading && (
            <main className="mb-auto">
              <QuoteStageThreePageLoadingState />
            </main>
          )}
          {!isLoading && (
            <main className="mb-auto pb-20 bg-surface/background">
              <QuoteFormTabBar
                tabOne={stage_one_tab_link}
                tabTwo={stage_two_tab_link}
                tabThree={stage_three_tab_label}
                activeTabNumber={3}
              />
              <Container startingBreakpoint="sm">
                {heading && (
                  <QuoteFormHeadingBar
                    heading={heading}
                    isBottomBorderRequired={true}
                  />
                )}
              </Container>
              <Container startingBreakpoint="sm">
                <Grid>
                  <Col
                    span={{ default: 12, xl: 5 }}
                    start={{ default: 1, xl: 1, xxl: 2 }}
                    order={{ default: 2, sm: 2, md: 2, lg: 1, xl: 1 }}
                  >
                    <div className="border-1 border-[#949597] shadow-lg py-6 px-10 bg-system/white rounded-lg xl:mt-6">
                      <h2 className="text-4xl mb-5">
                        Limited company, auction transaction or marital change?
                      </h2>
                      <p>
                        If you're making a purchase or sale through a limited
                        company, auction, or due to a change in your
                        relationship status, we'd love to chat! Please give us a
                        call on{' '}
                        <a
                          className="text-text/body-primary-highlight"
                          href="tel:0333 234 4425"
                        >
                          0333 234 4425.
                        </a>{' '}
                        There are some extra details we'd like to discuss to
                        ensure we tailor your quotation just for you.
                      </p>
                    </div>
                    <QuoteForm
                      sections={left_column}
                      onSubmit={handleSubmit}
                      initValues={{ ...quote?.customerDetails }}
                      propertyType={getPropertyType(quote?.transactionType)}
                    />
                    {apiErrors ? <ApiErrors errors={apiErrors} /> : null}
                  </Col>

                  <Col
                    span={{ default: 12, xl: 5, xxl: 4 }}
                    start={{ default: 1, xl: 8, xxl: 8 }}
                    order={{ default: 1, sm: 1, md: 1, lg: 2, xl: 2 }}
                  >
                    <div className="xl:space-y-8 flex flex-col-reverse xl:flex-col pt-6">
                      {right_column.map((blok: Blok) => (
                        <StoryblokComponent blok={blok} key={blok._uid} />
                      ))}
                    </div>
                  </Col>
                </Grid>
              </Container>
            </main>
          )}
          {footer && (
            <StoryblokComponent blok={footer.content} key={footer._uid} />
          )}
        </div>

        {isSubmitting && (
          <div
            className={`fixed top-6 right-7 cursor-pointer w-6 h-6 z-[60] transition-all duration-500`}
          >
            <div
              className={`overflow-auto fixed h-screen w-screen z-[59] bg-[#0B212E] bg-opacity-90 top-0 left-0 cursor-pointer py-0 transition-all duration-500 backdrop-blur-sm`}
            >
              <div className="relative container mx-auto justify-center h-full w-full py-40 grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-6 md:col-start-4 md:col-end-10 bg-blue-500 text-center items-center flex flex-col justify-center min-h-[200px]">
                  <img src={spinner} alt="loader" className="rotate" />
                  <Heading
                    headingType={HeadingTypes.h3}
                    text="Accepting your quote, just a sec..."
                    className="pt-4 !text-[white]"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <QuoteStageModal
          onClose={() =>
            dispatch(
              setModalState({
                isQuoteModalOpen: false,
                quoteModalContent: null,
              })
            )
          }
          toggleModal={modalState.isQuoteModalOpen}
        >
          <div className="flex flex-col gap-6">
            <div className="modal-item">
              {costBreakdown?.clauses.map((clause: Clause, i: number) => {
                return (
                  <div className="modal-item" key={`${clause.heading}${i}`}>
                    <h3 className="text-system/white text-2xl">
                      {clause.heading}
                    </h3>
                    <p className="text-system/white text-lg">
                      {clause.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </QuoteStageModal>

        <TestingGridMolecule />
      </PageContext.Provider>
    </QuoteContext.Provider>
  );
};

export default memo(QuoteStageThreePage);
